import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import moment from 'moment';
import { AuthContext } from '@app/providers';
import { currencyFormat, DownloadPDF, numberFormat } from '@app/utils';
import { SecondaryHeader, Button, Icon } from '@app/components';
import ActivityLogList from '@app/modules/activity-log';

import {
  UserDetails,
  PropertyDetails,
  InvolvedInfo,
  quesType,
  ActivityLogInfo,
  PaymentProps,
  ApproverDetails,
  VerifyTaxInfo,
} from '@app/types';

import {
  BackgroundContainer,
  Container,
  ListView,
  Loader,
  ListContainer,
  MunicipalityContainer,
  MunicipalityLogo,
  MunicipalityImage,
  InfoBox,
  Infocard,
  Label,
  PrimaryValue,
  Value,
  PropertyContainer,
  PropertyIcon,
  PropertyInfoBox,
  PrimaryInfocard,
  SecondaryInfocard,
  InfoContainer,
  PropertyNotesContainer,
  NotesCard,
  PurchaseValue,
  InvolvedContainer,
  Heading,
  ActivityHeading,
  InvolvedBox,
  InvolvedTitle,
  DetailsBox,
  DetailsCard,
  InvolvedDetailContainer,
  InvoledDetailBox,
  InvolvedImage,
  InvolvedLogo,
  Text,
  PrimaryText,
  SecondaryText,
  TaskListContainer,
  TaskListBox,
  TaskContainer,
  TaskIcon,
  TaskDetailBox,
  TaskCard,
  TaskLabel,
  Name,
  Status,
  ChecklistBox,
  ButtonContainer,
  TaskListInfoBox,
  MobileText,
  CheckListInstructionsBox,
  ChecklistImage,
  ChecklistImageContainer,
  FileName,
  ViewandDownload,
  ImageText,
  EditResponseContainer,
  EditResponseText,
  ChecklistBoldText,
  ModalInfocard,
  ChecklistInfoCard,
  AnsBoldText,
  DesktopSideNav,
  ProInfo,
  EqualValue,
  MunicipalityMobileLogo,
  PurchaseInfocard,
  InvolvedMobileLogo,
  ImageCard,
  StatusCard,
  CompletedDate,
  TaskInfoBox,
  ActivityLogMobileContainer,
  Error,
  ActivityMobileContainer,
  MobileContainer,
  MobileCreate,
  TitleCompanyLabel,
  TitleCompanyValue,
  DashedSeperator,
  Seperator,
  MlsLinkValue,
  ActivityLogContainer,
} from '@app/modules/transaction-details/transactions-details-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCircle,
  faCheckCircle,
  faPencilAlt,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import { useLocation } from '@app/utils';
import SideNav from './transactions-details-sidenav';
import AdditionalQuestionsModal from './additional-questions-modal';
import TaxProofModal from '@app/modules/transaction-details/tax-proof-modal';
import InspectionModal from '@app/modules/transaction-details/inspection-modal';
import WaterReadingModal from '@app/modules/transaction-details/water-reading-modal';
import LegalDescriptionModal from '@app/modules/transaction-details/legal-description-modal';
import VerifyTaxModal from '@app/modules/transaction-details/verify-tax-modal';
import MarkasCompletedModal from '@app/modules/transaction-details/mark-as-completed-modal';
import MarkAsApprovedModal from '@app/modules/transaction-details/mark-as-approved-modal';
import { useGetTransactionQuery, useUpdateDocuments } from '@app/graphql';
import { DocumentContainer } from '@app/modules/transaction-details/transactions-details-components';
import Documents from '@app/modules/transaction-form/documents';
import { UpdateDocType, UploadDocType } from '@app/types/documents';
import { removeDocument } from '@app/modules/transaction-form/transactions-form';
import { PaymentDueContainer } from '@app/modules/transaction-form/transactions-form-components';
import PaymentDue from '@app/modules/payment-due/payment-due';
import { addComma } from '@app/utils/tax-calculation';
import ActivityLogMobile from '../activity-log/activity-log-mobile';
import {
  approveTransaction,
  editTransaction,
  checkEditTransactionPermission,
} from '@app/utils/permissions';

const DOWNLOAD_URL = process.env.REACT_APP_DOWNLOAD_URL;

const PropertyIconStyles = {
  height: 46,
  width: 38,
  color: '#6A67CE',
};

const IconStyle = {
  width: 20,
  height: 20,
};

/**
 *
 * @param data users data
 * @param i index of the selected user
 * @returns filtered users
 */
const filterUser = (data: any, i: number) => {
  return data.user !== null;
};

/**
 *
 * @param props properties required to check list image
 * @returns
 */
const CheckListImage = (props: UserDetails) => {
  const { image, fileName, href, onClick } = props;
  return (
    <TaskContainer>
      <ChecklistImageContainer>
        <ChecklistImage image={image} />
        <ImageCard>
          <FileName>{fileName}</FileName>
          <ViewandDownload>
            <ImageText onClick={onClick}>View</ImageText>
            <ImageText>
              <a href={href} download>
                Download
              </a>
            </ImageText>
          </ViewandDownload>
        </ImageCard>
      </ChecklistImageContainer>
    </TaskContainer>
  );
};

/**
 *
 * @returns transaction details view
 */
export default function TranscationDetails() {
  const { navigate } = useLocation();
  const { transactionId }: any = useParams();
  const { userInfo }: any = useContext(AuthContext);
  const { loading, data } = useGetTransactionQuery({
    id: transactionId ? transactionId : '',
    offSetData: new Date().getTimezoneOffset().toString(),
  });
  const [updateDocuments] = useUpdateDocuments();
  const [pendingTasks, setPendingTasks] = React.useState<string>('');
  const [approverDetails, setApproverDetails] = React.useState<ApproverDetails>(
    {
      isApproved: false,
      approvedBy: '',
      approvedDate: '',
      approver: '',
      status: '',
    },
  );
  const [transactionCode, setTransactionCode] = React.useState<string>('');
  const [transactionStatus, setTransactionStatus] = React.useState<string>('');
  const [paymentStatus, setPaymentStatus] = React.useState<boolean>(false);
  const [municipalityDetails, setMunicipalityDetails] = React.useState({
    name: '',
    logo: '',
    waterReadingInst: '',
    inspInst: '',
    inspDoc: '',
    taxProofInst: '',
    verifyTaxInst: '',
    taxRate: '',
  });
  const [creatorDetails, setCreatorDetails] = React.useState<UserDetails>({
    name: '',
    createdOn: '',
    type: '',
    firebaseId: '',
  });
  const [propertyDetails, setPropertyDetails] = React.useState<PropertyDetails>(
    {
      type: '',
      address: '',
      zip: '',
      purchasePrice: '',
      closingDate: '',
      notes: '',
      county: '',
    },
  );
  const [mlsLink, setMlsLink] = React.useState('');
  const [involvedDetails, setInvolvedDetails] = React.useState<InvolvedInfo>({
    buyers: [],
    sellers: [],
    professionalUsers: [],
    assignmentChecklist: {},
  });

  const [legalDesDetails, setLegalDesDetails] = React.useState({
    user: {},
    userType: '',
    status: '',
    notes: '',
    firebaseId: '',
    completedAt: '',
    showLegalDes: false,
  });
  const [waterReadDetails, setWaterReadDetails] = React.useState({
    user: {},
    userType: '',
    status: '',
    completedAt: '',
    firebaseId: '',
    finalReadingAvailNow: 'no',
    FinalReadingRequested: 'no',
    dateForFinalReading: moment().format('YYYY-MM-DD'),
    image: '',
    fileName: '',
    showWaterReading: false,
  });
  const [additionalQuestions, setAdditionalQuestions] = React.useState<
    quesType
  >({
    completedAt: moment().format('YYYY-MM-DD'),
    status: '',
    userType: '',
    firebaseId: '',
    answers: [],
    user: {},
    showQues: false,
  });
  const [taxPaymentProof, setTaxPaymentProof] = React.useState({
    image: '',
    fileName: '',
    status: '',
    userType: '',
    completedAt: '',
    firebaseId: '',
    taxPaymentReceiptNumber: '',
    dateofTaxPayment: '',
    user: {},
    showTaxProof: false,
  });

  const [verifyTax, setVerifyTax] = React.useState<VerifyTaxInfo>({
    status: '',
    userType: '',
    completedAt: moment().format('YYYY-MM-DD'),
    firebaseId: '',
    verified: false,
    taxAmount: 0,
    user: {},
    showVerifyTax: false,
    exemptReasons: [],
    exemptDetails: '',
    exemptImage: '',
    fileName: '',
  });

  const [inspectionData, setInspectionData] = React.useState({
    completedAt: moment().format('YYYY-MM-DD'),
    status: '',
    userType: '',
    firebaseId: '',
    user: {},
    image: '',
    fileName: '',
    inspectionNotes: '',
    showInspection: false,
  });
  const [transactionPayments, setTransactionPayments] = React.useState<
    PaymentProps[]
  >([]);
  const [taxExemption, setTaxExemption] = React.useState('');
  const [activityLog, setActivityLog] = React.useState<ActivityLogInfo[]>([]);
  const [showActivityLog, setShowActivityLog] = React.useState(false);
  const [showWaterReadModal, setShowWaterReadModal] = React.useState<boolean>(
    false,
  );
  const [showLegalDesModal, setShowLegalDesModal] = React.useState<boolean>(
    false,
  );
  const [showInspectionModal, setInspectionModal] = React.useState<boolean>(
    false,
  );
  const [showVerifyTaxModal, setVerifyTaxModal] = React.useState<boolean>(
    false,
  );
  const [showQuesModal, setShowQuesModal] = React.useState<boolean>(false);
  const [showImage, setShowImage] = React.useState<boolean>(false);
  const [showtaxProofImage, setShowTaxProofImage] = React.useState<boolean>(
    false,
  );
  const [showInspImage, setShowInspImage] = React.useState<boolean>(false);
  const [showTaxProofModal, setTaxProofModal] = React.useState<boolean>(false);
  const [showExemptImage, setShowExemptImage] = React.useState<boolean>(false);
  const [showMarkasCompleted, setShowMarkasCompleted] = React.useState<boolean>(
    false,
  );
  const [showApproveModal, setShowApprovedModal] = React.useState<boolean>(
    false,
  );
  const [titleCompany, setTitleCompany] = React.useState('');
  const [documents, setDocuments] = React.useState<UploadDocType[]>([]);
  const [transactionData, setTransactionData] = React.useState([]);
  const [exportText, setExportText] = React.useState('Export');
  const [showErrorMessage, setShowErrorMessage] = React.useState(false);

  React.useEffect(() => {
    if (
      !loading &&
      data &&
      data.getTransactionDetails &&
      data.getTransactionDetails.success
    ) {
      if (data.getTransactionDetails.transaction) {
        const {
          getTransactionDetails: { transaction },
        } = data;
        setTransactionData(transaction);
        getStatus(transaction.status);
        setTransactionCode(transaction.transactionCode);
        setPendingTasks(transaction.pendingTasks);
        setApproverDetails({
          approvedBy: transaction.approvedBy,
          approvedDate: transaction.approvedDate,
          isApproved: transaction.isApproved,
          approver: transaction.approvedInfo
            ? transaction.approvedInfo.firstName +
              ' ' +
              transaction.approvedInfo.lastName
            : '',
          status: transaction.status,
        });
        getPaymentStatus(transaction.transactionPayments);
        setTransactionPayments(transaction.transactionPayments);
        const {
          municipalityData: {
            waterReading = {},
            inspection = {},
            proofofTaxPayment = {},
            TaxCalculations = {},
          },
        } = transaction;
        setTitleCompany(transaction.titleCompany || '--');
        setTaxExemption(
          transaction.municipalityData &&
            transaction.municipalityData.taxExemption
            ? transaction.municipalityData.taxExemption
            : '',
        );
        setMunicipalityDetails({
          name: transaction.municipalityData.name,
          logo: transaction.municipalityData.logo,
          waterReadingInst: waterReading.instructions,
          inspInst: inspection.instructions,
          inspDoc: inspection.allowUsersToUploadDocs,
          taxProofInst: proofofTaxPayment.instructions,
          verifyTaxInst: TaxCalculations.instructions,
          taxRate: TaxCalculations.rate,
        });
        const {
          creator: { type, firebaseId, user = {} },
        } = transaction;
        if (user) {
          const { firstName, lastName } = user;
          setCreatorDetails({
            name: `${firstName} ${lastName}`,
            createdOn: transaction.createdAt,
            type: type,
            firebaseId: firebaseId,
          });
        }
        setDocuments(transaction.transactionDocuments);
        const {
          property: {
            address,
            city,
            state,
            zipCode,
            purchasePrice,
            closingDate,
            notes,
            type: propertyType,
          },
          countyInfo,
        } = transaction;
        setMlsLink(transaction.mlsLink);
        setPropertyDetails({
          type: propertyType,
          address: `${address}, ${city}, ${state}`,
          zip: zipCode,
          purchasePrice: purchasePrice,
          closingDate: closingDate
            ? moment(closingDate).format('MMM D, YYYY')
            : '--',
          notes: notes,
          county: countyInfo && countyInfo.label ? countyInfo.label : '',
        });
        setInvolvedDetails({
          buyers: transaction.buyers,
          sellers: transaction.sellers,
          professionalUsers: transaction.professionalUsers,
          assignmentChecklist: transaction.assignmentChecklist,
        });
        const { pendingTasks = '' } = transaction;
        loadChecklistData(transaction.assignmentChecklist, pendingTasks);
        getActivityLogData(transaction.activityLog);
      }
    }
    if (
      !loading &&
      data &&
      data.getTransactionDetails &&
      !data.getTransactionDetails.success
    ) {
      setShowErrorMessage(true);
    }
    // eslint-disable-next-line
  }, [loading, data]);
  const openMlsLink = () => {
    window.open(mlsLink);
  };
  const loadChecklistData = (data: any, pendingTasks: string) => {
    setActivityLog(activityLog);
    setPendingTasks(pendingTasks);
    if (data && data.legalDescription) {
      const { legalDescription = {} } = data;
      const {
        user = {},
        userType = '',
        status = '',
        notes = '',
        completedAt = moment().format('YYYY-MM-DD'),
        firebaseId = '',
      } = legalDescription;
      setLegalDesDetails({
        user: user,
        userType: userType,
        status: status,
        notes: notes,
        firebaseId: firebaseId,
        completedAt: completedAt,
        showLegalDes: status ? true : false,
      });
    }
    if (data && data.waterReading) {
      const { waterReading = {} } = data;
      const {
        user = {},
        status = '',
        isFinalReadingAvailNow = 'no',
        isFinalReadingRequested = 'no',
        readingDocuments = [],
        dateForFinalReading = moment().format('YYYY-MM-DD'),
        completedAt = moment().format('YYYY-MM-DD'),
        firebaseId = '',
        userType = '',
      } = waterReading;
      let selectedImage =
        readingDocuments && readingDocuments.length
          ? readingDocuments[0].src
          : '';
      let selectedFileName =
        readingDocuments && readingDocuments.length
          ? readingDocuments[0].name
          : '';

      setWaterReadDetails({
        user: user,
        userType: userType,
        status: status,
        completedAt: completedAt,
        firebaseId: firebaseId,
        finalReadingAvailNow: isFinalReadingAvailNow
          ? isFinalReadingAvailNow
          : 'no',
        FinalReadingRequested: isFinalReadingRequested
          ? isFinalReadingRequested
          : 'no',
        dateForFinalReading: dateForFinalReading
          ? moment(dateForFinalReading).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD'),
        image: selectedImage,
        fileName: selectedFileName,
        showWaterReading: status ? true : false,
      });
    }
    if (data && data.additionalQuestions) {
      const { additionalQuestions = {} } = data;
      const {
        completedAt = moment().format('YYYY-MM-DD'),
        firebaseId: additionalQuesfirebaseId = '',
        answers = [],
        status = '',
        user = {},
        userType = '',
      } = additionalQuestions;
      setAdditionalQuestions({
        completedAt: completedAt,
        firebaseId: additionalQuesfirebaseId,
        answers: answers,
        status: status,
        user: user,
        userType: userType,
        showQues: answers && answers.length ? true : false,
      });
    }
    if (data && data.proofofTaxPayment) {
      const { proofofTaxPayment: taxProofData = {} } = data;
      const {
        status: taxProofStatus = '',
        completedAt: taxProofCompletedAt = moment().format('YYYY-MM-DD'),
        firebaseId: taxProoffirebaseId = '',
        taxPaymentReceiptNumber = '',
        dateofTaxPayment = '',
        user: taxProofUser = {},
        userType = '',
        proofofTaxPaymentDocuments = [],
      } = taxProofData;
      const selectedImage =
        proofofTaxPaymentDocuments &&
        proofofTaxPaymentDocuments.length &&
        proofofTaxPaymentDocuments[0].src
          ? proofofTaxPaymentDocuments[0].src
          : '';
      const selectedFileName =
        proofofTaxPaymentDocuments &&
        proofofTaxPaymentDocuments.length &&
        proofofTaxPaymentDocuments[0].name
          ? proofofTaxPaymentDocuments[0].name
          : '';
      setTaxPaymentProof({
        image: selectedImage,
        fileName: selectedFileName,
        status: taxProofStatus,
        completedAt: taxProofCompletedAt
          ? moment(taxProofCompletedAt).format('MMM D, YYYY')
          : '--',
        firebaseId: taxProoffirebaseId,
        taxPaymentReceiptNumber: taxPaymentReceiptNumber,
        dateofTaxPayment: dateofTaxPayment,
        user: taxProofUser,
        userType: userType,
        showTaxProof: taxProofStatus ? true : false,
      });
    }
    if (data && data.inspection) {
      const { inspection: inspectionObj = {} } = data;
      const {
        completedAt: inspCompletedAt = moment().format('YYYY-MM-DD'),
        user: inspUser = {},
        firebaseId: inspfirebaseId = '',
        status: inspStatus = '',
        inspectionNotes = '',
        userType = '',
        inspectionDocuments = [],
      } = inspectionObj;
      setInspectionData({
        completedAt: inspCompletedAt
          ? moment(inspCompletedAt).format('MMM D, YYY')
          : '--',
        status: inspStatus,
        firebaseId: inspfirebaseId,
        user: inspUser,
        userType: userType,
        image:
          inspectionDocuments && inspectionDocuments.length
            ? inspectionDocuments[0].src
            : '',
        fileName:
          inspectionDocuments && inspectionDocuments.length
            ? inspectionDocuments[0].name
            : '',
        inspectionNotes: inspectionNotes,
        showInspection: inspStatus ? true : false,
      });
    }
    if (data && data.verifyTaxCalculations) {
      const { verifyTaxCalculations = {} } = data;
      const {
        status = '',
        userType = '',
        firebaseId = '',
        user = {},
        taxAmount = 0,
        completedAt = moment().format('YYYY-MM-DD'),
        verified = false,
        exemption = {},
      } = verifyTaxCalculations;

      setVerifyTax({
        status: status ? status : '',
        completedAt: completedAt ? completedAt : moment().format('YYYY-MM-DD'),
        firebaseId: firebaseId,
        taxAmount: taxAmount,
        user: user,
        userType: userType ? userType : '',
        verified: verified,
        showVerifyTax: status ? true : false,
        exemptReasons:
          exemption && exemption.reasons && exemption.reasons.length
            ? exemption.reasons
            : [],
        exemptDetails: exemption && exemption.details ? exemption.details : '',
        exemptImage:
          exemption &&
          exemption.exemptionDocuments &&
          exemption.exemptionDocuments.length
            ? exemption.exemptionDocuments[0].src
            : '',
        fileName:
          exemption &&
          exemption.exemptionDocuments &&
          exemption.exemptionDocuments.length
            ? exemption.exemptionDocuments[0].name
            : '',
      });
    }
  };
  const getStatus = (status: string) => {
    setTransactionStatus(status);
  };
  const getPaymentStatus = (payments: PaymentProps[]) => {
    if (payments && payments.length) {
      const unPaidPayments: PaymentProps[] = payments.filter(
        (payment: PaymentProps) => {
          return payment.paymentStatus === false;
        },
      );
      if (unPaidPayments && unPaidPayments.length && transactionId) {
        setPaymentStatus(false);
      } else {
        setPaymentStatus(true);
      }
    }
  };
  const getActivityLogData = (activitylog: ActivityLogInfo[]) => {
    setActivityLog(activitylog);
  };

  function updateFiles(params: UpdateDocType) {
    updateDocuments({ variables: params })
      .then((response: any) => {})
      .catch((error: any) => {});
  }
  let docs: any = [];
  function getFiles(filesData: UploadDocType[]) {
    const activityLogs: ActivityLogInfo[] = activityLogCreation('Documents');
    if (filesData && filesData.length) {
      docs = filesData.map((file: UploadDocType) => {
        return {
          title: file.title,
          docUrl: file.docUrl || file.Location,
          docKey: file.docKey,
          firebaseId:
            userInfo && userInfo.firebaseId ? userInfo.firebaseId : '',
          notes: file.notes,
          docUniqueId: file.docUniqueId,
          documentType: file.documentType || '',
          docContentType: file.docContentType || '',
          otherDocumentType: file.otherDocumentType,
          uploadedDate: file.uploadedDate || moment(),
          uploadedBy: userInfo
            ? userInfo.firstName + ' ' + userInfo.lastName
            : '',
        };
      });
      let params: UpdateDocType = {
        transactionId: transactionId,
        transactionDocuments: docs,
        activityLog: activityLogs,
      };
      updateFiles(params);
      setDocuments(docs);
    }
  }
  function removeFromS3(removedDoc: UploadDocType[]) {
    return removedDoc.map((rF: UploadDocType) => {
      return removeDocument(rF);
    });
  }
  function activityLogCreation(item: string) {
    let activityLogs: ActivityLogInfo[] = [];
    const pushObj: any = {
      transactionId: transactionId,
      firebaseId: userInfo && userInfo.firebaseId ? userInfo.firebaseId : '',
      item: item,
      description: `has been updated by`,
    };
    activityLogs.push(pushObj);
    const logs: ActivityLogInfo[] = activityLog;
    logs.unshift({
      ...pushObj,
      user: userInfo,
    });
    setActivityLog(logs);
    return activityLogs;
  }

  function afterRemove(docs: UploadDocType[], removedDoc: UploadDocType[]) {
    const activityLogs: ActivityLogInfo[] = activityLogCreation('Documents');
    let params: UpdateDocType = {
      transactionId: transactionId,
      transactionDocuments: docs,
      activityLog: activityLogs,
    };
    removeFromS3(removedDoc);
    setDocuments(docs);
    updateFiles(params);
  }

  async function generatePdf() {
    setExportText('Exporting...');
    const data = {
      transactionId: transactionId,
      transactionData: transactionData,
    };
    await DownloadPDF(data);
    setExportText('Export');
  }

  const checkApproveStatus = () => {
    if (
      approveTransaction(userInfo, approverDetails.isApproved) &&
      paymentStatus &&
      !pendingTasks
    ) {
      return true;
    }
    return false;
  };

  const checkEditPermission = () => {
    if (
      transactionStatus !== 'APPROVED' &&
      (editTransaction(userInfo, transactionId) ||
        checkEditTransactionPermission(userInfo))
    ) {
      return true;
    }
    return false;
  };

  const checkShowPrimaryButton = () => {
    if (!pendingTasks) {
      return getButtonText() === 'Approve' || getButtonText() === 'Unapprove'
        ? checkApproveStatus()
        : true;
    }
    return false;
  };
  const statusColor = () => {
    return transactionStatus === 'OPEN'
      ? 'primary'
      : transactionStatus === 'INPROGRESS'
      ? 'magento'
      : 'success';
  };
  const getButtonText = () => {
    return transactionStatus === 'APPROVED'
      ? 'Unapprove'
      : transactionStatus === 'COMPLETED'
      ? 'Approve'
      : 'Request For Approval';
  };
  const primaryButtonClick = () => {
    return transactionStatus === 'COMPLETED' || transactionStatus === 'APPROVED'
      ? setShowApprovedModal(true)
      : setShowMarkasCompleted(true);
  };
  return (
    <>
      <BackgroundContainer>
        <Container>
          {loading ? (
            <Loader>
              <Icon name="loader" />
            </Loader>
          ) : showErrorMessage ? (
            <Error>
              Something went wrong...
              <div onClick={() => navigate('/transactions')}>Go to home</div>
            </Error>
          ) : (
            <>
              <SecondaryHeader
                showMenuBar={true}
                title={municipalityDetails.name}
                statusValue={transactionStatus}
                showButton={checkEditPermission()}
                dataLabel={transactionCode}
                onBackClick={() =>
                  approverDetails.isApproved
                    ? navigate('/transactions/approved')
                    : navigate('/transactions')
                }
                showLabel={true}
                showStatus={true}
                statusColor={statusColor()}
                primaryButtonText={getButtonText()}
                showText={pendingTasks ? true : false}
                text={
                  parseInt(pendingTasks) > 1
                    ? `${pendingTasks} Tasks Pending`
                    : `${pendingTasks} Task Pending`
                }
                approveButton={
                  approverDetails.isApproved ? 'Unapprove' : 'Approve'
                }
                showApproveBackground={true}
                showApproveButton={checkApproveStatus()}
                secondaryButtonText="Edit"
                showSecondaryBackground={true}
                optionalButtonText={exportText}
                showOptionalButton={true}
                showPrimaryButton={checkShowPrimaryButton()}
                disabledPrimaryButton={pendingTasks ? true : false}
                onSecondaryButtonClick={() =>
                  navigate(`/transactions/edit/${transactionId}`)
                }
                onOptionalButtonClick={() => generatePdf()}
                onApproveButtonClick={() => {
                  setShowApprovedModal(true);
                }}
                onPrimaryButtonClick={() => {
                  primaryButtonClick();
                }}
                primaryMobileButtonText={getButtonText()}
                onPrimaryMobileButtonClick={() => {
                  primaryButtonClick();
                }}
                disabledMobilePrimaryButton={pendingTasks ? true : false}
                showMobilePrimaryButton={checkShowPrimaryButton()}
              />
              <DesktopSideNav>
                <SideNav
                  userInfo={userInfo}
                  setShowActivityLog={setShowActivityLog}
                />
              </DesktopSideNav>
              {showActivityLog ? (
                <ActivityLogContainer id="activityLogDetails">
                  <ActivityHeading>
                    <div>Activity Log</div>
                  </ActivityHeading>
                  <ActivityLogList activityLog={activityLog} />
                </ActivityLogContainer>
              ) : (
                <ListView id="transactionDetailContainer">
                  <ListContainer id="municipalityDetails">
                    <MunicipalityContainer>
                      <MunicipalityLogo>
                        <MunicipalityImage image={municipalityDetails.logo} />
                      </MunicipalityLogo>
                      <InfoBox>
                        <Infocard>
                          <Label>Municipality</Label>
                          <PrimaryValue>
                            {municipalityDetails.name}
                          </PrimaryValue>
                        </Infocard>
                        <PrimaryInfocard>
                          <Label>Transaction Created by</Label>
                          <Value>
                            {`${creatorDetails.name} on ${
                              creatorDetails.createdOn
                                ? moment(creatorDetails.createdOn).format(
                                    'MMM D, YYYY',
                                  )
                                : '--'
                            }`}
                          </Value>
                        </PrimaryInfocard>
                        {approverDetails.isApproved && (
                          <PrimaryInfocard>
                            <Label>Transaction Approved by</Label>
                            <Value>
                              {`${approverDetails.approver} on ${
                                approverDetails.approvedDate
                                  ? moment(approverDetails.approvedDate).format(
                                      'MMM D, YYYY hh:mm A',
                                    )
                                  : '--'
                              }`}
                            </Value>
                          </PrimaryInfocard>
                        )}
                      </InfoBox>
                      <MunicipalityMobileLogo>
                        <MunicipalityImage image={municipalityDetails.logo} />
                      </MunicipalityMobileLogo>
                    </MunicipalityContainer>
                    <PropertyContainer id="propertyDetails">
                      <PropertyIcon>
                        <FontAwesomeIcon
                          icon={faBuilding}
                          style={PropertyIconStyles}
                        />
                      </PropertyIcon>
                      <InfoContainer>
                        <PropertyInfoBox>
                          <Infocard>
                            <Label>Property Info</Label>
                            <PrimaryValue>{propertyDetails.type}</PrimaryValue>
                            <Value>{propertyDetails.county},</Value>
                            <Value>{propertyDetails.address},</Value>
                            <Value>{propertyDetails.zip}.</Value>
                          </Infocard>
                          <ProInfo>
                            <PurchaseInfocard>
                              <Label>Purchase Price </Label>
                              <PurchaseValue>
                                {propertyDetails.purchasePrice.includes('$')
                                  ? propertyDetails.purchasePrice
                                  : `$${currencyFormat(
                                      propertyDetails.purchasePrice,
                                    )}`}
                              </PurchaseValue>
                            </PurchaseInfocard>
                            <SecondaryInfocard>
                              <Label>Closing Date </Label>
                              <Value>{propertyDetails.closingDate}</Value>
                            </SecondaryInfocard>
                          </ProInfo>
                        </PropertyInfoBox>
                        <PropertyNotesContainer>
                          <NotesCard>
                            <Label>Notes</Label>
                            <Value>
                              {propertyDetails.notes
                                ? propertyDetails.notes
                                : '- -'}
                            </Value>
                          </NotesCard>
                        </PropertyNotesContainer>
                        <PropertyNotesContainer>
                          <NotesCard>
                            <Label>MLS Link</Label>
                            <MlsLinkValue onClick={() => openMlsLink()}>
                              {mlsLink || '--'}
                            </MlsLinkValue>
                          </NotesCard>
                        </PropertyNotesContainer>
                      </InfoContainer>
                    </PropertyContainer>
                    <DocumentContainer id="documentsDetails">
                      <Documents
                        fromEditPage={true}
                        uploadedDocs={documents}
                        getFiles={getFiles}
                        afterRemove={afterRemove}
                        isApproved={approverDetails.isApproved}
                      />
                      <Seperator />
                    </DocumentContainer>
                    <InvolvedContainer id="involvedDetails">
                      <Heading>Who is Involved?</Heading>
                      <TitleCompanyLabel>Title Company</TitleCompanyLabel>
                      <TitleCompanyValue>{titleCompany}</TitleCompanyValue>
                      <DashedSeperator />
                      <InvolvedBox>
                        <InvolvedTitle>Buyer Details</InvolvedTitle>
                        <DetailsBox>
                          {involvedDetails.buyers.map((data, i) => {
                            return (
                              <DetailsCard key={i}>
                                <InvolvedDetails
                                  name={data.name}
                                  email={data.email}
                                  phone={data.phone}
                                  address={data.address}
                                  subDetails={data.subDetails}
                                />
                              </DetailsCard>
                            );
                          })}
                        </DetailsBox>
                      </InvolvedBox>

                      <InvolvedBox>
                        <InvolvedTitle>Seller Details</InvolvedTitle>
                        <DetailsBox>
                          {involvedDetails.sellers.map((data, i) => {
                            return (
                              <DetailsCard key={i}>
                                <InvolvedDetails
                                  name={data.name}
                                  email={data.email}
                                  phone={data.phone}
                                  address={data.address}
                                  subDetails={data.subDetails}
                                />
                              </DetailsCard>
                            );
                          })}
                        </DetailsBox>
                      </InvolvedBox>

                      <InvolvedBox>
                        <InvolvedTitle>
                          Professional Users Details
                        </InvolvedTitle>
                        <DetailsBox>
                          {involvedDetails.professionalUsers
                            .filter(filterUser)
                            .map((data, i) => {
                              return (
                                <DetailsCard key={i}>
                                  <InvolvedDetails
                                    name={` ${data.user.firstName} ${data.user.lastName}`}
                                    email={`${data.type}, ${data.user.company}`}
                                    phone={
                                      data.user.phone ? data.user.phone : '- -'
                                    }
                                    address={data.user.email}
                                    image={data.user.profilePicUrl}
                                    subDetails={data.subDetails}
                                  />
                                </DetailsCard>
                              );
                            })}
                        </DetailsBox>
                      </InvolvedBox>
                    </InvolvedContainer>
                    <TaskListContainer id="checklistDetails">
                      <Heading>Task Checklist</Heading>
                      <TaskListBox>
                        {legalDesDetails.showLegalDes && (
                          <ChecklistBox
                            color={
                              legalDesDetails.status === 'COMPLETED'
                                ? 'success'
                                : 'secondary'
                            }>
                            <TaskListDetails
                              title="Legal Description"
                              name={getCheckListUserName(
                                legalDesDetails.user,
                                creatorDetails,
                                involvedDetails,
                              )}
                              status={legalDesDetails.status}
                              showButton={
                                legalDesDetails.firebaseId ===
                                  userInfo.firebaseId &&
                                legalDesDetails.status !== 'COMPLETED'
                              }
                              date={
                                legalDesDetails.status === 'COMPLETED'
                                  ? legalDesDetails.completedAt
                                    ? moment(
                                        legalDesDetails.completedAt,
                                      ).format('MMM D, Y')
                                    : ''
                                  : ''
                              }
                              onClick={() => {
                                setShowLegalDesModal(true);
                              }}
                            />
                            {legalDesDetails.status === 'COMPLETED' && (
                              <CheckListInstructionsBox>
                                <ModalInfocard>
                                  {legalDesDetails.notes
                                    ? legalDesDetails.notes
                                        .split('\n')
                                        .map((data, i) => {
                                          return <Value key={i}>{data}</Value>;
                                        })
                                    : ''}
                                </ModalInfocard>
                                {legalDesDetails.firebaseId ===
                                  userInfo.firebaseId &&
                                  legalDesDetails.status === 'COMPLETED' &&
                                  transactionStatus !== 'COMPLETED' && (
                                    <EditResponseContainer
                                      onClick={() => {
                                        setShowLegalDesModal(true);
                                      }}>
                                      <EditResponseText>
                                        Edit Response
                                      </EditResponseText>
                                    </EditResponseContainer>
                                  )}
                              </CheckListInstructionsBox>
                            )}
                          </ChecklistBox>
                        )}
                        {verifyTax.showVerifyTax && (
                          <ChecklistBox
                            color={
                              verifyTax.status === 'COMPLETED'
                                ? 'success'
                                : 'secondary'
                            }>
                            <TaskListDetails
                              title="Verify Tax Calculations"
                              name={getCheckListUserName(
                                verifyTax.user,
                                creatorDetails,
                                involvedDetails,
                              )}
                              status={verifyTax.status}
                              showButton={
                                verifyTax.firebaseId === userInfo.firebaseId &&
                                verifyTax.status !== 'COMPLETED'
                              }
                              date={
                                verifyTax.status !== 'COMPLETED'
                                  ? ''
                                  : verifyTax.completedAt
                                  ? moment(verifyTax.completedAt).format(
                                      'MMM DD, Y',
                                    )
                                  : ''
                              }
                              onClick={() => {
                                setVerifyTaxModal(true);
                              }}
                            />
                            {verifyTax.status === 'COMPLETED' && (
                              <CheckListInstructionsBox>
                                <InfoContainer>
                                  {verifyTax.verified && (
                                    <ModalInfocard>
                                      <Infocard>
                                        <Value> Verified Tax Amount</Value>
                                        <ChecklistBoldText>
                                          $
                                          {addComma(
                                            verifyTax.taxAmount.toFixed(2),
                                          )}
                                        </ChecklistBoldText>
                                      </Infocard>
                                    </ModalInfocard>
                                  )}
                                  {verifyTax.verified === false ? (
                                    <>
                                      {verifyTax.exemptReasons &&
                                      verifyTax.exemptReasons.length ? (
                                        <>
                                          <ChecklistBoldText>
                                            Exemption Reasons
                                          </ChecklistBoldText>
                                          {verifyTax.exemptReasons
                                            .filter(e => !!e.checked)
                                            .map((exempt, i) => {
                                              return (
                                                <ModalInfocard key={i}>
                                                  <ChecklistInfoCard>
                                                    <EqualValue>
                                                      {i + 1}.
                                                    </EqualValue>
                                                    <Value>{`${exempt.exemption}`}</Value>
                                                  </ChecklistInfoCard>
                                                </ModalInfocard>
                                              );
                                            })}
                                        </>
                                      ) : null}
                                      {verifyTax.exemptDetails && (
                                        <ModalInfocard>
                                          <ChecklistBoldText>
                                            Exemption Details
                                          </ChecklistBoldText>
                                          <Value>
                                            {verifyTax.exemptDetails}
                                          </Value>
                                        </ModalInfocard>
                                      )}
                                      {verifyTax.exemptImage && (
                                        <CheckListImage
                                          image={verifyTax.exemptImage}
                                          fileName={verifyTax.fileName}
                                          href={`${DOWNLOAD_URL}downloadFile?key=${verifyTax.exemptImage}`}
                                          onClick={() => {
                                            setShowExemptImage(true);
                                          }}
                                        />
                                      )}
                                      {showExemptImage && (
                                        <Lightbox
                                          mainSrc={verifyTax.exemptImage}
                                          onCloseRequest={() =>
                                            setShowExemptImage(false)
                                          }
                                        />
                                      )}
                                    </>
                                  ) : null}
                                </InfoContainer>

                                {verifyTax.firebaseId === userInfo.firebaseId &&
                                  verifyTax.status === 'COMPLETED' &&
                                  transactionStatus !== 'COMPLETED' && (
                                    <EditResponseContainer
                                      onClick={() => {
                                        setVerifyTaxModal(true);
                                      }}>
                                      <EditResponseText>
                                        Edit Response
                                      </EditResponseText>
                                    </EditResponseContainer>
                                  )}
                              </CheckListInstructionsBox>
                            )}
                          </ChecklistBox>
                        )}
                        {waterReadDetails.showWaterReading && (
                          <ChecklistBox
                            color={
                              waterReadDetails.status === 'COMPLETED'
                                ? 'success'
                                : 'secondary'
                            }>
                            <TaskListDetails
                              title="Water Reading"
                              name={getCheckListUserName(
                                waterReadDetails.user,
                                creatorDetails,
                                involvedDetails,
                              )}
                              status={waterReadDetails.status}
                              showButton={
                                waterReadDetails.firebaseId ===
                                  userInfo.firebaseId &&
                                waterReadDetails.status !== 'COMPLETED'
                              }
                              onClick={() => {
                                setShowWaterReadModal(true);
                              }}
                              date={
                                waterReadDetails.status !== 'COMPLETED'
                                  ? ''
                                  : waterReadDetails.completedAt
                                  ? waterReadDetails.completedAt
                                  : ''
                              }
                            />
                            {waterReadDetails.status === 'COMPLETED' && (
                              <CheckListInstructionsBox>
                                <div>
                                  {waterReadDetails.image &&
                                    waterReadDetails.finalReadingAvailNow ===
                                      'yes' && (
                                      <CheckListImage
                                        image={waterReadDetails.image}
                                        fileName={waterReadDetails.fileName}
                                        href={` ${DOWNLOAD_URL}downloadFile?key=${waterReadDetails.image}`}
                                        onClick={() => {
                                          setShowImage(true);
                                        }}
                                      />
                                    )}
                                </div>
                                {showImage && (
                                  <Lightbox
                                    mainSrc={waterReadDetails.image}
                                    onCloseRequest={() => setShowImage(false)}
                                  />
                                )}
                                {!waterReadDetails.image &&
                                  waterReadDetails.finalReadingAvailNow ===
                                    'yes' && (
                                    <Infocard>
                                      <Value>
                                        Is the final water reading available
                                        now?
                                      </Value>
                                      <ChecklistBoldText>Yes</ChecklistBoldText>
                                    </Infocard>
                                  )}
                                {waterReadDetails.finalReadingAvailNow ===
                                  'no' && (
                                  <TaskContainer>
                                    {waterReadDetails.FinalReadingRequested ===
                                      'yes' && (
                                      <Infocard>
                                        <Value>
                                          {' '}
                                          Date for final water reading
                                        </Value>
                                        <ChecklistBoldText>
                                          {waterReadDetails.dateForFinalReading
                                            ? moment(
                                                waterReadDetails.dateForFinalReading,
                                              ).format('MMM D, YYYY')
                                            : '--'}
                                        </ChecklistBoldText>
                                      </Infocard>
                                    )}
                                    {waterReadDetails.FinalReadingRequested ===
                                      'no' && (
                                      <Infocard>
                                        <Value>
                                          Has the final water reading been
                                          requested?
                                        </Value>
                                        <ChecklistBoldText>
                                          No
                                        </ChecklistBoldText>
                                      </Infocard>
                                    )}
                                  </TaskContainer>
                                )}
                                {waterReadDetails.firebaseId ===
                                  userInfo.firebaseId &&
                                  waterReadDetails.status === 'COMPLETED' &&
                                  transactionStatus !== 'COMPLETED' && (
                                    <EditResponseContainer
                                      onClick={() => {
                                        setShowWaterReadModal(true);
                                      }}>
                                      <EditResponseText>
                                        Edit Response
                                      </EditResponseText>
                                    </EditResponseContainer>
                                  )}
                              </CheckListInstructionsBox>
                            )}
                          </ChecklistBox>
                        )}
                        {inspectionData.showInspection && (
                          <ChecklistBox
                            color={
                              inspectionData.status === 'COMPLETED'
                                ? 'success'
                                : 'secondary'
                            }>
                            <TaskListDetails
                              title="Inspection"
                              name={getCheckListUserName(
                                inspectionData.user,
                                creatorDetails,
                                involvedDetails,
                              )}
                              status={inspectionData.status}
                              showButton={
                                inspectionData.firebaseId ===
                                  userInfo.firebaseId &&
                                inspectionData.status !== 'COMPLETED'
                              }
                              date={
                                inspectionData.status === 'COMPLETED'
                                  ? inspectionData.completedAt
                                  : ''
                              }
                              onClick={() => {
                                setInspectionModal(true);
                              }}
                            />
                            {inspectionData.status === 'COMPLETED' && (
                              <CheckListInstructionsBox>
                                <InfoContainer>
                                  <ModalInfocard>
                                    <ChecklistBoldText>Notes</ChecklistBoldText>
                                    <Value>
                                      {inspectionData.inspectionNotes}
                                    </Value>
                                  </ModalInfocard>
                                  {inspectionData.image && (
                                    <CheckListImage
                                      image={inspectionData.image}
                                      fileName={inspectionData.fileName}
                                      href={`${DOWNLOAD_URL}downloadFile?key=${inspectionData.image}`}
                                      onClick={() => {
                                        setShowInspImage(true);
                                      }}
                                    />
                                  )}
                                  {showInspImage && (
                                    <Lightbox
                                      mainSrc={inspectionData.image}
                                      onCloseRequest={() =>
                                        setShowInspImage(false)
                                      }
                                    />
                                  )}
                                </InfoContainer>
                                {inspectionData.firebaseId ===
                                  userInfo.firebaseId &&
                                  inspectionData.status === 'COMPLETED' &&
                                  transactionStatus !== 'COMPLETED' && (
                                    <EditResponseContainer
                                      onClick={() => {
                                        setInspectionModal(true);
                                      }}>
                                      <EditResponseText>
                                        Edit Response
                                      </EditResponseText>
                                    </EditResponseContainer>
                                  )}
                              </CheckListInstructionsBox>
                            )}
                          </ChecklistBox>
                        )}

                        {additionalQuestions.showQues && (
                          <ChecklistBox
                            color={
                              additionalQuestions.status === 'COMPLETED'
                                ? 'success'
                                : 'secondary'
                            }>
                            <TaskListDetails
                              title="Additional Questions"
                              name={getCheckListUserName(
                                additionalQuestions.user,
                                creatorDetails,
                                involvedDetails,
                              )}
                              date={
                                additionalQuestions.status === 'COMPLETED'
                                  ? additionalQuestions.completedAt
                                  : ''
                              }
                              status={additionalQuestions.status}
                              showButton={
                                additionalQuestions.firebaseId ===
                                  userInfo.firebaseId &&
                                additionalQuestions.status !== 'COMPLETED'
                              }
                              onClick={() => {
                                setShowQuesModal(true);
                              }}
                            />
                            {additionalQuestions.status === 'COMPLETED' && (
                              <CheckListInstructionsBox>
                                <InfoContainer>
                                  {additionalQuestions &&
                                    additionalQuestions.answers.map(
                                      (ques, i) => {
                                        return (
                                          <TaskContainer key={i}>
                                            <ModalInfocard>
                                              <ChecklistInfoCard>
                                                <EqualValue>
                                                  {i + 1}.
                                                </EqualValue>
                                                <Value>{`${ques.question}`}</Value>
                                              </ChecklistInfoCard>
                                              <AnsBoldText>
                                                {ques.answer
                                                  ? ques.answer
                                                  : '  - -'}
                                              </AnsBoldText>
                                            </ModalInfocard>
                                          </TaskContainer>
                                        );
                                      },
                                    )}
                                </InfoContainer>
                                {additionalQuestions.firebaseId ===
                                  userInfo.firebaseId &&
                                  additionalQuestions.status === 'COMPLETED' &&
                                  transactionStatus !== 'COMPLETED' && (
                                    <EditResponseContainer
                                      onClick={() => {
                                        setShowQuesModal(true);
                                      }}>
                                      <EditResponseText>
                                        Edit Response
                                      </EditResponseText>
                                    </EditResponseContainer>
                                  )}
                              </CheckListInstructionsBox>
                            )}
                          </ChecklistBox>
                        )}
                        {taxPaymentProof.showTaxProof && (
                          <ChecklistBox
                            color={
                              taxPaymentProof.status === 'COMPLETED'
                                ? 'success'
                                : 'secondary'
                            }>
                            <TaskListDetails
                              title="Proof of Tax Payment"
                              name={getCheckListUserName(
                                taxPaymentProof.user,
                                creatorDetails,
                                involvedDetails,
                              )}
                              status={taxPaymentProof.status}
                              showButton={
                                taxPaymentProof.firebaseId ===
                                  userInfo.firebaseId &&
                                taxPaymentProof.status !== 'COMPLETED'
                              }
                              date={
                                taxPaymentProof.status === 'COMPLETED'
                                  ? taxPaymentProof.completedAt
                                  : ''
                              }
                              onClick={() => {
                                setTaxProofModal(true);
                              }}
                            />
                            {taxPaymentProof &&
                              taxPaymentProof.status &&
                              taxPaymentProof.status === 'COMPLETED' && (
                                <CheckListInstructionsBox>
                                  <InfoContainer>
                                    <Infocard>
                                      <Value> Receipt Number</Value>
                                      <ChecklistBoldText>
                                        {taxPaymentProof.taxPaymentReceiptNumber
                                          ? taxPaymentProof.taxPaymentReceiptNumber
                                          : '  - -'}
                                      </ChecklistBoldText>
                                    </Infocard>

                                    <Infocard>
                                      <Value>Date of Payment</Value>
                                      <ChecklistBoldText>
                                        {taxPaymentProof.dateofTaxPayment
                                          ? moment(
                                              taxPaymentProof.dateofTaxPayment,
                                            ).format('MMM D, YYYY')
                                          : '--'}
                                      </ChecklistBoldText>
                                    </Infocard>

                                    {taxPaymentProof.image && (
                                      <CheckListImage
                                        image={taxPaymentProof.image}
                                        fileName={taxPaymentProof.fileName}
                                        href={` ${DOWNLOAD_URL}downloadFile?key=${taxPaymentProof.image}`}
                                        onClick={() => {
                                          setShowTaxProofImage(true);
                                        }}
                                      />
                                    )}

                                    {showtaxProofImage && (
                                      <Lightbox
                                        mainSrc={taxPaymentProof.image}
                                        onCloseRequest={() =>
                                          setShowTaxProofImage(false)
                                        }
                                      />
                                    )}
                                  </InfoContainer>
                                  {taxPaymentProof.firebaseId ===
                                    userInfo.firebaseId &&
                                    taxPaymentProof.status === 'COMPLETED' &&
                                    transactionStatus !== 'COMPLETED' && (
                                      <EditResponseContainer
                                        onClick={() => {
                                          setTaxProofModal(true);
                                        }}>
                                        <EditResponseText>
                                          Edit Response
                                        </EditResponseText>
                                      </EditResponseContainer>
                                    )}
                                </CheckListInstructionsBox>
                              )}
                          </ChecklistBox>
                        )}
                      </TaskListBox>
                    </TaskListContainer>
                    <PaymentDueContainer
                      fromDetailsPage={true}
                      id="paymentDetails">
                      <Heading>Payment Due</Heading>
                      <PaymentDue
                        payments={transactionPayments}
                        userInfo={userInfo}
                        allowEdit={false}
                        transactionId={transactionId}
                        fromDetailsTab={true}
                        activityLog={activityLog}
                        setCostItems={setTransactionPayments}
                        setActivityLog={setActivityLog}
                      />
                    </PaymentDueContainer>
                    <ActivityLogMobileContainer>
                      <ActivityHeading>
                        <div>Activity Log</div>
                        <div>
                          <FontAwesomeIcon icon={faFilter} size="1x" />
                        </div>
                      </ActivityHeading>
                      <ActivityMobileContainer>
                        <ActivityLogMobile logs={activityLog} />
                      </ActivityMobileContainer>
                    </ActivityLogMobileContainer>
                  </ListContainer>
                  {showWaterReadModal && (
                    <WaterReadingModal
                      waterReadingInst={municipalityDetails.waterReadingInst}
                      waterReadingData={waterReadDetails}
                      userFirebaseId={userInfo.firebaseId}
                      status={waterReadDetails.status}
                      loadChecklistData={loadChecklistData}
                      getActivityLogData={getActivityLogData}
                      onModalClose={() => {
                        setShowWaterReadModal(false);
                      }}
                    />
                  )}
                  {showLegalDesModal && (
                    <LegalDescriptionModal
                      onModalClose={() => {
                        setShowLegalDesModal(false);
                      }}
                      legalData={legalDesDetails}
                      userFirebaseId={userInfo.firebaseId}
                      loadChecklistData={loadChecklistData}
                      getActivityLogData={getActivityLogData}
                      status={legalDesDetails.status}
                    />
                  )}
                  {showQuesModal && (
                    <AdditionalQuestionsModal
                      onModalClose={() => {
                        setShowQuesModal(false);
                      }}
                      Questions={additionalQuestions.answers}
                      userFirebaseId={userInfo.firebaseId}
                      loadChecklistData={loadChecklistData}
                      getActivityLogData={getActivityLogData}
                      status={additionalQuestions.status}
                      userType={additionalQuestions.userType}
                    />
                  )}
                  {showTaxProofModal && (
                    <TaxProofModal
                      onModalClose={() => {
                        setTaxProofModal(false);
                      }}
                      instructions={municipalityDetails.taxProofInst}
                      userFirebaseId={userInfo.firebaseId}
                      TaxProofData={taxPaymentProof}
                      loadChecklistData={loadChecklistData}
                      getActivityLogData={getActivityLogData}
                      status={taxPaymentProof.status}
                    />
                  )}
                  {showInspectionModal && (
                    <InspectionModal
                      onModalClose={() => {
                        setInspectionModal(false);
                      }}
                      instructions={municipalityDetails.inspInst}
                      docs={municipalityDetails.inspDoc}
                      userFirebaseId={userInfo.firebaseId}
                      loadChecklistData={loadChecklistData}
                      getActivityLogData={getActivityLogData}
                      data={inspectionData}
                      status={inspectionData.status}
                    />
                  )}
                  {showVerifyTaxModal && (
                    <VerifyTaxModal
                      payments={transactionPayments}
                      taxExemption={taxExemption}
                      onModalClose={() => {
                        setVerifyTaxModal(false);
                      }}
                      userFirebaseId={userInfo.firebaseId}
                      taxInst={municipalityDetails.verifyTaxInst}
                      taxRate={municipalityDetails.taxRate}
                      purchasePrice={propertyDetails.purchasePrice}
                      loadChecklistData={loadChecklistData}
                      status={verifyTax.status}
                      exemptionData={verifyTax}
                      getActivityLogData={getActivityLogData}
                    />
                  )}
                  {showMarkasCompleted && (
                    <MarkasCompletedModal
                      onModalClose={() => {
                        setShowMarkasCompleted(false);
                      }}
                      userFirebaseId={userInfo.firebaseId}
                      getStatus={getStatus}
                      getActivityLogData={getActivityLogData}
                      transactionCode={transactionCode}
                    />
                  )}
                  {showApproveModal && (
                    <MarkAsApprovedModal
                      approverDetails={approverDetails}
                      setApproverDetails={setApproverDetails}
                      onModalClose={() => {
                        setShowApprovedModal(false);
                      }}
                      setTransactionStatus={setTransactionStatus}
                      userInfo={userInfo}
                      getActivityLogData={getActivityLogData}
                      transactionCode={transactionCode}
                    />
                  )}
                  {userInfo.firebaseId === creatorDetails.firebaseId &&
                    transactionStatus !== 'COMPLETED' &&
                    userInfo.userType === 'professional_user' && (
                      <>
                        <MobileContainer>
                          <MobileCreate
                            onClick={() =>
                              navigate(`/transactions/edit/${transactionId}`)
                            }>
                            <FontAwesomeIcon
                              color="#fff"
                              icon={faPencilAlt}
                              style={IconStyle}
                            />
                          </MobileCreate>
                        </MobileContainer>
                      </>
                    )}
                </ListView>
              )}
            </>
          )}
        </Container>
      </BackgroundContainer>
    </>
  );
}

/**
 *
 * @param data data rquired to get activity user name
 * @returns user name of activity log
 */
export const getActivityUserName = (data: any) => {
  let userName: string = '';
  if (data) {
    const { firstName = '', lastName = '' } = data;
    return `${firstName} ${lastName}`;
  } else {
    return userName;
  }
};

/**
 *
 * @param props involved user details
 * @returns
 */
export const InvolvedDetails = (props: UserDetails) => {
  const { name, email, phone, address, subDetails, image } = props;
  return (
    <InvolvedDetailContainer>
      <InvoledDetailBox>
        <InvolvedImage>
          <InvolvedLogo image={image} />
        </InvolvedImage>
        <TaskInfoBox>
          <Text>{name}</Text>
          {subDetails?.contactNameForBusiness && (
            <Text>{subDetails.contactNameForBusiness}</Text>
          )}
          <PrimaryText>{email}</PrimaryText>
          <MobileText>{numberFormat(phone)}</MobileText>
          <SecondaryText>{address}</SecondaryText>
        </TaskInfoBox>
        <InvolvedImage>
          <InvolvedMobileLogo image={image} />
        </InvolvedImage>
      </InvoledDetailBox>
    </InvolvedDetailContainer>
  );
};

/**
 *
 * @param props check list details
 * @returns
 */
export const TaskListDetails = (props: UserDetails) => {
  const { title, name, status, date, showButton, onClick } = props;
  return (
    <TaskContainer>
      <TaskIcon>
        <FontAwesomeIcon
          icon={status === 'COMPLETED' ? faCheckCircle : faCircle}
        />
      </TaskIcon>
      <TaskDetailBox>
        <TaskCard>
          <TaskLabel>{title}</TaskLabel>
          <Name>{name} </Name>
        </TaskCard>
        <TaskListInfoBox>
          <StatusCard>
            <Status color={status === 'COMPLETED' ? 'success' : 'secondary'}>
              {status}
            </Status>
            {date && (
              <CompletedDate>
                On {moment(date).format('MMM D, YYYY')}
              </CompletedDate>
            )}
          </StatusCard>
          {showButton && (
            <ButtonContainer>
              <Button
                variant="warning"
                size="medium"
                width="100%"
                type="submit"
                onClick={onClick}>
                Submit Task
              </Button>
            </ButtonContainer>
          )}
        </TaskListInfoBox>
      </TaskDetailBox>
    </TaskContainer>
  );
};

/**
 *
 * @param data user data
 * @returns check list assigned user name
 */
export const getCheckListUserName = (
  data: any,
  creatorDetails: UserDetails,
  involvedDetails: InvolvedInfo,
) => {
  let checklistName: string = '';
  if (data) {
    if (creatorDetails.name === `${data.firstName} ${data.lastName}`) {
      checklistName = `${creatorDetails.name} - ${creatorDetails.type}`;
    } else {
      const selectedList = involvedDetails.professionalUsers.find(
        (d: any) =>
          d.user &&
          d.user.firstName === data.firstName &&
          d.user.lastName === data.lastName,
      );
      if (selectedList) {
        const {
          user: { firstName = '', lastName = '' } = {
            firstName: '',
            lastName: '',
          },
          type = '',
        } = selectedList;
        checklistName = `${firstName} ${lastName} - ${type}`;
      } else {
        checklistName = 'User not assigned';
      }
    }
  } else {
    checklistName = 'User not assigned';
  }
  return checklistName;
};
